import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import { configGetConfig } from "@/api/config/configApiConfig";
import { APP_CONFIG_REGISTRY } from "@/constants/appConfigRegistry";

export type ConfigEntryStatus = "idle" | "loading" | "succeeded" | "failed";

export type ConfigEntryState = {
  status: ConfigEntryStatus;
  data: unknown;
  error: string | null;
};

export type AppConfigState = {
  entries: Record<string, ConfigEntryState>;
};

const createInitialEntries = (): Record<string, ConfigEntryState> =>
  Object.fromEntries(
    APP_CONFIG_REGISTRY.map(({ name }) => [name, { status: "idle", data: null, error: null }])
  );

const initialState: AppConfigState = {
  entries: createInitialEntries()
};

type FetchAppConfigArgs = {
  apiEnv: string;
  accessToken: string;
  configName: string;
  version: string;
};

export const fetchAppConfig = createAsyncThunk(
  "appConfig/fetch",
  async ({ apiEnv, accessToken, configName, version }: FetchAppConfigArgs, { rejectWithValue }) => {
    try {
      const response = await axios(configGetConfig(apiEnv, accessToken, configName, version));
      return { configName, data: response.data };
    } catch (error) {
      if (error instanceof AxiosError) {
        const message =
          error.response?.data?.Error ??
          error.response?.data?.error ??
          `Failed to load config "${configName}"`;
        return rejectWithValue({ configName, error: message });
      }
      if (error instanceof Error) {
        return rejectWithValue({ configName, error: error.message });
      }
      return rejectWithValue({ configName, error: `Failed to load config "${configName}"` });
    }
  }
);

const ensureConfigEntry = (state: AppConfigState, configName: string): ConfigEntryState => {
  if (!state.entries[configName]) {
    state.entries[configName] = { status: "idle", data: null, error: null };
  }
  return state.entries[configName];
};

const configSlice = createSlice({
  name: "appConfig",
  initialState,
  reducers: {
    /** Seeds/updates an entry from the cross-tab session cache without a network call. */
    hydrateAppConfig: (state, action: PayloadAction<{ configName: string; data: unknown }>) => {
      const entry = ensureConfigEntry(state, action.payload.configName);
      entry.status = "succeeded";
      entry.data = action.payload.data;
      entry.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppConfig.pending, (state, action) => {
        const entry = ensureConfigEntry(state, action.meta.arg.configName);
        // A cached (hydrated) entry stays usable while it is refetched in the background.
        if (entry.status !== "succeeded") {
          entry.status = "loading";
        }
        entry.error = null;
      })
      .addCase(fetchAppConfig.fulfilled, (state, action) => {
        const entry = ensureConfigEntry(state, action.payload.configName);
        entry.status = "succeeded";
        entry.data = action.payload.data;
        entry.error = null;
      })
      .addCase(fetchAppConfig.rejected, (state, action) => {
        const configName = action.meta.arg.configName;
        const entry = ensureConfigEntry(state, configName);
        const payload = action.payload as { configName: string; error: string } | undefined;
        entry.error = payload?.error ?? `Failed to load config "${configName}"`;
        // Keep serving cached data if the background refetch fails.
        if (entry.status !== "succeeded") {
          entry.status = "failed";
          entry.data = null;
        }
      });
  }
});

export const { hydrateAppConfig } = configSlice.actions;

export default configSlice.reducer;
